import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCurrentUser, selectUserId, selectUsers } from '../login/loginSlice';
import {
  assignEmployeeCompany,
  deleteBusinessCompany,
  getBusinessCompanies,
  getBusinessEmployees,
  getEmployeeAccessibleCompanies,
  getEmployeeAccessibleChats,
  getEmployeeCompanyAccess,
  getUserData,
  removeEmployeeCompanyAccess,
  saveEmployeeCompanyAccess,
} from '../login/loginApi';
import { applyAccountSwitch, resolveBusinessAccountId } from '../../utils/accountUtils';
import {
  arrowLIcon,
  arrowRIcon,
  closeIconTask,
  dots3VerticalIcon,
  addAccountIcon,
  deleteIcon,
  iconWithPlus,
} from '../../static/svgs';
import { url } from '../../static/env';
import ClientMenuModal from '../client/ClientMenuModal';
import AddCompanyModal from '../client/AddCompanyModal';
import '../shared/companyPicker.css';
import '../client/client.css';
import './profile.css';

const DOC_ACCESS_SECTIONS = [
  { id: 'invoices', title: 'Счета-фактуры, УПД, акты, накладные, чеки' },
  { id: 'bank', title: 'Банковские выписки' },
  { id: 'tax', title: 'Налоги, декларации' },
  { id: 'salary', title: 'Заработная плата, взносы' },
  { id: 'buh_bill', title: 'Счета за бухгалтерские услуги' },
];

function mapDocAccessFromApi(access) {
  const docs = {
    invoices: false,
    bank: false,
    tax: false,
    salary: false,
    buh_bill: false,
  };
  DOC_ACCESS_SECTIONS.forEach(({ id }) => {
    docs[id] = !!(access?.[id] ?? access?.[`access_${id}`]);
  });
  if (access?.documents && !DOC_ACCESS_SECTIONS.some(({ id }) => docs[id])) {
    DOC_ACCESS_SECTIONS.forEach(({ id }) => {
      docs[id] = true;
    });
  }
  return docs;
}

function asBoolFlag(value) {
  if (value == null) return false;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const s = String(value).trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'yes';
}

function mapOwnedToItem(company, businessUserId) {
  const legacyId = company.legacy_user_id != null ? Number(company.legacy_user_id) : null;
  return {
    user_id: legacyId || Number(company.company_id),
    company_name: company.name,
    company_id: Number(company.company_id),
    legacy_user_id: legacyId,
    inn: company.inn,
    address: company.address,
    is_company_record: true,
    is_company_context: true,
    access_mode: 'owner',
    business_user_id: businessUserId != null ? Number(businessUserId) : null,
    has_legacy_user: !!legacyId,
    in_work: asBoolFlag(company.in_work),
    on_service: asBoolFlag(company.on_service),
    employee_user_id: null,
  };
}

function mapDelegatedToItem(company, employeeUserId) {
  const legacyId = company.legacy_user_id != null ? Number(company.legacy_user_id) : null;
  const companyId = Number(company.company_id);
  const guestId = employeeUserId != null ? Number(employeeUserId) : null;
  return {
    user_id: legacyId || companyId,
    company_name: company.name,
    company_id: companyId,
    legacy_user_id: legacyId,
    inn: company.inn || null,
    address: company.address || null,
    is_company_record: true,
    is_company_context: true,
    access_mode: 'delegated',
    business_user_id: guestId,
    company_owner_user_id: company.business_user_id != null ? Number(company.business_user_id) : null,
    accountant_id: company.accountant_id || null,
    has_legacy_user: !!legacyId,
    in_work: asBoolFlag(company.in_work),
    on_service: asBoolFlag(company.on_service),
    employee_user_id: guestId,
    access_documents: !!company.access_documents,
    access_invoices: !!company.access_invoices,
    access_bank: !!company.access_bank,
    access_tax: !!company.access_tax,
    access_salary: !!company.access_salary,
    access_buh_bill: !!company.access_buh_bill,
    access_tasks: !!company.access_tasks,
    access_chat: !!company.access_chat,
    owner_photo_path: company.owner_photo_path || null,
    owner_name: company.owner_name || null,
  };
}

function mapApiEmployee(row) {
  const companies = (row.companies || []).map((c) => ({
    companyId: String(c.company_id),
    legacyUserId: c.legacy_user_id != null ? String(c.legacy_user_id) : null,
    name: c.name || `Компания #${c.company_id}`,
    inn: c.inn || null,
  }));
  return {
    id: String(row.id),
    employeeUserId: row.employee_user_id,
    displayName: row.name || row.phone || `Пользователь ${row.employee_user_id}`,
    phone: row.phone,
    status: row.status,
    companies,
  };
}

/** Каталог компаний в Профиле: свои + делегированные, добавление по ИНН. */
function ProfileCompanies({ onBack }) {
  const dispatch = useDispatch();
  const users = useSelector(selectUsers);
  const currentUser = useSelector(selectCurrentUser);
  const userId = useSelector(selectUserId);

  const businessAccountId = useMemo(
    () => resolveBusinessAccountId({ users, currentUser, userId }),
    [users, currentUser, userId]
  );

  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showAddByInn, setShowAddByInn] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [detailMenuOpen, setDetailMenuOpen] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [client, setClient] = useState({});
  const [removingCompany, setRemovingCompany] = useState(false);

  const [accessEmployee, setAccessEmployee] = useState(null);
  const [accessDraft, setAccessDraft] = useState(null);
  const [accessBaseline, setAccessBaseline] = useState(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [userAccessMenuOpen, setUserAccessMenuOpen] = useState(false);
  const [grantedChats, setGrantedChats] = useState([]);
  const [grantedChatsLoading, setGrantedChatsLoading] = useState(false);

  const loadCompanies = useCallback(async () => {
    if (!businessAccountId) {
      setCompanies([]);
      return;
    }
    setLoading(true);
    try {
      const [ownedResult, delegatedResult] = await Promise.all([
        getBusinessCompanies(businessAccountId),
        getEmployeeAccessibleCompanies(businessAccountId, 'all'),
      ]);
      const byId = new Map();
      if (ownedResult.success) {
        (ownedResult.companies || []).forEach((c) => {
          if (!c.company_id) return;
          byId.set(Number(c.company_id), mapOwnedToItem(c, businessAccountId));
        });
      }
      if (delegatedResult.success) {
        (delegatedResult.companies || []).forEach((c) => {
          if (!c.company_id) return;
          const id = Number(c.company_id);
          if (byId.has(id)) return;
          byId.set(id, mapDelegatedToItem(c, businessAccountId));
        });
      }
      const merged = [...byId.values()].sort((a, b) =>
        String(a.company_name || '').localeCompare(String(b.company_name || ''), 'ru')
      );
      setCompanies(merged);
    } catch (e) {
      console.error(e);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [businessAccountId]);

  const loadEmployees = useCallback(async () => {
    if (!businessAccountId) {
      setEmployees([]);
      return;
    }
    try {
      const result = await getBusinessEmployees(businessAccountId);
      if (result.success) {
        setEmployees((result.employees || []).map(mapApiEmployee));
      } else {
        setEmployees([]);
      }
    } catch (e) {
      console.error(e);
      setEmployees([]);
    }
  }, [businessAccountId]);

  useEffect(() => {
    loadCompanies();
    loadEmployees();
  }, [loadCompanies, loadEmployees]);

  useEffect(() => {
    if (!showDetail || selectedCompany?.access_mode !== 'delegated') {
      setGrantedChats([]);
      setGrantedChatsLoading(false);
      return undefined;
    }
    const employeeId = businessAccountId;
    const companyId = selectedCompany?.company_id;
    if (!employeeId || !companyId) {
      setGrantedChats([]);
      return undefined;
    }
    let cancelled = false;
    setGrantedChatsLoading(true);
    getEmployeeAccessibleChats(employeeId, {
      companyId,
      legacyUserId: selectedCompany.legacy_user_id,
    })
      .then((result) => {
        if (!cancelled) setGrantedChats(result?.chats || []);
      })
      .catch(() => {
        if (!cancelled) setGrantedChats([]);
      })
      .finally(() => {
        if (!cancelled) setGrantedChatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showDetail, selectedCompany, businessAccountId]);

  useEffect(() => {
    if (!showDetail || !selectedCompany) return;
    if (!selectedCompany.has_legacy_user) {
      setClient({
        name: selectedCompany.company_name,
        inn: selectedCompany.inn,
        address: selectedCompany.address,
      });
      return;
    }
    getUserData(selectedCompany.user_id)
      .then((data) => setClient(data?.user?.[0] || {}))
      .catch(() => setClient({}));
  }, [showDetail, selectedCompany]);

  const companyUsers = useMemo(() => {
    if (!selectedCompany) return [];
    const companyId = String(selectedCompany.company_id);
    return employees.filter((e) =>
      e.companies.some((c) => String(c.companyId) === companyId)
    );
  }, [employees, selectedCompany]);

  const usersToAdd = useMemo(() => {
    if (!selectedCompany) return [];
    const companyId = String(selectedCompany.company_id);
    return employees.filter(
      (e) => !e.companies.some((c) => String(c.companyId) === companyId)
    );
  }, [employees, selectedCompany]);

  const getAccessLabel = (company) => {
    if (company?.access_mode === 'delegated') {
      const name = String(company.owner_name || '').trim();
      return name
        ? `Предоставлен пользователем: ${name}`
        : 'Предоставлен пользователем';
    }
    const companyId = company?.company_id ?? company;
    const hasUsers = employees.some((e) =>
      e.companies.some((c) => String(c.companyId) === String(companyId))
    );
    return hasUsers ? 'У меня и разрешенных пользователей' : 'Только у меня';
  };

  const getServiceStatusLabel = (onService) =>
    asBoolFlag(onService)
      ? 'На постоянном бухгалтерском обслуживании'
      : 'Может перейти на постоянное обслуживание';

  const accessLabel = selectedCompany
    ? getAccessLabel(selectedCompany)
    : 'Только у меня';

  const serviceStatusLabel = getServiceStatusLabel(selectedCompany?.on_service);

  const SERVICE_INFO_TIP =
    'Для перехода компании на постоянное бухгалтерское обслуживание, необходимо написать об этом письмо в чате и согласовать все условия';

  const renderServiceInfoHint = () => (
    <span className="profile-status-info" aria-label={SERVICE_INFO_TIP}>
      <span className="profile-status-info-btn" aria-hidden="true">
        i
      </span>
      <span className="profile-status-info-tip" role="tooltip">
        {SERVICE_INFO_TIP}
      </span>
    </span>
  );

  const handleSelect = (user) => {
    setSelectedCompany(user);
    applyAccountSwitch(dispatch, user);
    setShowDetail(true);
    setAccessEmployee(null);
    setAccessDraft(null);
    setAccessBaseline(null);
  };

  const openUserAccess = async (employee) => {
    if (!selectedCompany || !employee) return;
    setAccessLoading(true);
    setAccessEmployee(employee);
    try {
      const result = await getEmployeeCompanyAccess(
        businessAccountId || userId,
        employee.employeeUserId,
        {
          companyId: selectedCompany.company_id
            ? Number(selectedCompany.company_id)
            : undefined,
          legacyUserId: selectedCompany.legacy_user_id
            ? Number(selectedCompany.legacy_user_id)
            : undefined,
        }
      );
      if (!result.success) {
        alert(result.error || 'Не удалось загрузить доступы');
        setAccessEmployee(null);
        return;
      }
      const draft = {
        companyId: result.company.company_id,
        name: result.company.name,
        ...mapDocAccessFromApi(result.access),
        tasks: !!result.access.tasks,
        chats: (result.chats || []).map((ch) => ({
          id: ch.id,
          title: ch.title,
          enabled: !!ch.enabled,
        })),
      };
      setAccessDraft(draft);
      setAccessBaseline(JSON.parse(JSON.stringify(draft)));
    } catch (e) {
      console.error(e);
      alert('Ошибка загрузки доступов');
      setAccessEmployee(null);
    } finally {
      setAccessLoading(false);
    }
  };

  const handleAddUserToCompany = async (employee) => {
    if (!selectedCompany || !employee) return;
    setLoading(true);
    try {
      const result = await assignEmployeeCompany(
        businessAccountId || userId,
        employee.employeeUserId,
        {
          companyId: selectedCompany.company_id
            ? Number(selectedCompany.company_id)
            : undefined,
          legacyUserId: selectedCompany.legacy_user_id
            ? Number(selectedCompany.legacy_user_id)
            : undefined,
        }
      );
      if (!result.success) {
        alert(result.error || 'Не удалось добавить пользователя');
        return;
      }
      setShowAddUserModal(false);
      await loadEmployees();
      await openUserAccess(employee);
    } catch (e) {
      console.error(e);
      alert('Ошибка добавления пользователя');
    } finally {
      setLoading(false);
    }
  };

  const accessDirty = useMemo(() => {
    if (!accessDraft || !accessBaseline) return false;
    return JSON.stringify(accessDraft) !== JSON.stringify(accessBaseline);
  }, [accessDraft, accessBaseline]);

  const handleCancelAccess = () => {
    if (accessBaseline) {
      setAccessDraft(JSON.parse(JSON.stringify(accessBaseline)));
    }
  };

  const handleSaveAccess = async () => {
    if (!accessEmployee || !accessDraft) return;
    setAccessSaving(true);
    try {
      const result = await saveEmployeeCompanyAccess({
        business_user_id: businessAccountId || userId,
        employee_user_id: accessEmployee.employeeUserId,
        company_id: accessDraft.companyId,
        invoices: !!accessDraft.invoices,
        bank: !!accessDraft.bank,
        tax: !!accessDraft.tax,
        salary: !!accessDraft.salary,
        buh_bill: !!accessDraft.buh_bill,
        access_tasks: accessDraft.tasks,
        chat_ids: accessDraft.chats.filter((c) => c.enabled).map((c) => c.id),
      });
      if (!result.success) {
        alert(result.error || 'Не удалось сохранить');
        return;
      }
      setAccessBaseline(JSON.parse(JSON.stringify(accessDraft)));
      await loadEmployees();
    } catch (e) {
      console.error(e);
      alert('Ошибка сохранения доступов');
    } finally {
      setAccessSaving(false);
    }
  };

  const closeAccessEditor = () => {
    if (accessDirty && !window.confirm('Есть несохранённые изменения. Закрыть без сохранения?')) {
      return;
    }
    setAccessEmployee(null);
    setAccessDraft(null);
    setAccessBaseline(null);
    setAccessLoading(false);
    setUserAccessMenuOpen(false);
  };

  const handleRemoveCompany = async () => {
    if (!selectedCompany?.company_id || !businessAccountId || removingCompany) return;
    const name = selectedCompany.company_name || 'компанию';
    const confirmed = window.confirm(`Удалить компанию ${name} из своего аккаунта?`);
    if (!confirmed) return;

    setRemovingCompany(true);
    try {
      const result =
        selectedCompany.access_mode === 'delegated'
          ? await removeEmployeeCompanyAccess(businessAccountId, selectedCompany.company_id)
          : await deleteBusinessCompany(businessAccountId, selectedCompany.company_id);
      if (!result.success) {
        alert(result.error || 'Не удалось удалить компанию');
        return;
      }
      setDetailMenuOpen(false);
      setShowDetail(false);
      setSelectedCompany(null);
      setAccessEmployee(null);
      setAccessDraft(null);
      await loadCompanies();
    } catch (e) {
      console.error(e);
      alert('Ошибка удаления компании');
    } finally {
      setRemovingCompany(false);
    }
  };

  const handleRemoveUserFromCompany = async () => {
    if (!accessEmployee || !selectedCompany?.company_id) return;
    const name = accessEmployee.displayName || 'пользователя';
    const confirmed = window.confirm(
      `Удалить пользователя ${name} из этой компании?`
    );
    if (!confirmed) return;

    setLoading(true);
    try {
      const result = await removeEmployeeCompanyAccess(
        accessEmployee.employeeUserId,
        selectedCompany.company_id
      );
      if (!result.success) {
        alert(result.error || 'Не удалось удалить пользователя из компании');
        return;
      }
      setUserAccessMenuOpen(false);
      setAccessEmployee(null);
      setAccessDraft(null);
      setAccessBaseline(null);
      setAccessLoading(false);
      await loadEmployees();
    } catch (e) {
      console.error(e);
      alert('Ошибка удаления пользователя из компании');
    } finally {
      setLoading(false);
    }
  };

  const handleListBack = () => {
    if (accessEmployee || accessDraft || accessLoading) {
      if (accessDirty && !window.confirm('Есть несохранённые изменения. Закрыть без сохранения?')) {
        return;
      }
      setAccessEmployee(null);
      setAccessDraft(null);
      setAccessBaseline(null);
      setAccessLoading(false);
      setShowDetail(false);
      setSelectedCompany(null);
      return;
    }
    if (showDetail) {
      setShowDetail(false);
      setSelectedCompany(null);
      return;
    }
    onBack?.();
  };

  const headerTitle = showDetail
    ? selectedCompany?.company_name || 'Компания'
    : 'Все компании';

  const showUserAccess = !!(accessLoading || accessDraft || accessEmployee);

  const ChevronLeftIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path d="M15 6L9 12L15 18" stroke="#3262FF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const renderUserAccessNav = () => (
    <div className="profile-user-access-nav">
      <div className="profile-user-access-nav-row">
        <button
          type="button"
          className="profile-user-access-back"
          onClick={closeAccessEditor}
          aria-label="Назад"
        >
          <ChevronLeftIcon />
        </button>
        <div className="profile-user-access-heading">
          <div className="profile-user-access-name manrope-bold">
            {accessEmployee?.displayName || accessDraft?.name || 'Пользователь'}
          </div>
          {accessEmployee?.phone ? (
            <div className="profile-user-access-phone manrope-bold">{accessEmployee.phone}</div>
          ) : null}
        </div>
        <button
          type="button"
          className="profile-user-access-menu-btn"
          aria-label="Меню"
          onClick={() => setUserAccessMenuOpen(true)}
        >
          {dots3VerticalIcon}
        </button>
      </div>
      <div className="profile-settings-title manrope-bold">Доступ к разделам сервиса</div>
    </div>
  );

  const renderCompanyInfoRows = () => (
    <>
      <div className="client_row">
        <div className="client_row-label color_grey">Статус</div>
        <div className="client_row-value profile-status-value">
          <span>{serviceStatusLabel}</span>
          {!asBoolFlag(selectedCompany?.on_service) ? renderServiceInfoHint() : null}
        </div>
      </div>
      <div className="client_row">
        <div className="client_row-label color_grey">Доступ</div>
        <div className="client_row-value">{accessLabel}</div>
      </div>
    </>
  );

  const renderCompanyUsersBlock = () => (
    <div className="profile-company-users">
      <div className="profile-company-users-title manrope-bold">
        Пользователи, имеющие доступ к компании
      </div>
      {companyUsers.length === 0 ? (
        <div className="profile-empty profile-empty--compact manrope-bold">
          Пользователи не добавлены
        </div>
      ) : (
        <div className="company-picker-list company-picker-panel">
          {companyUsers.map((employee) => (
            <div key={employee.id} className="company-picker-item">
              <button
                type="button"
                className="company-picker-main-btn manrope-bold"
                onClick={() => openUserAccess(employee)}
              >
                <div className="company-picker-main-top">
                  <span className="company-picker-name">{employee.displayName}</span>
                  <span className="company-picker-trailing profile-employee-trailing">
                    {employee.phone ? (
                      <span className="profile-employee-meta profile-employee-meta--inline">
                        Тел: {employee.phone}
                      </span>
                    ) : null}
                    {arrowRIcon}
                  </span>
                </div>
              </button>
            </div>
          ))}
        </div>
      )}
      {selectedCompany?.access_mode !== 'delegated' ? (
        <button
          type="button"
          className="profile-add-company-btn manrope-bold"
          onClick={() => setShowAddUserModal(true)}
        >
          Добавить пользователя
        </button>
      ) : null}
    </div>
  );

  const renderGrantedAccessBlock = () => {
    const docs = DOC_ACCESS_SECTIONS.filter(
      (section) => !!selectedCompany?.[`access_${section.id}`]
    );
    const hasTasks = !!selectedCompany?.access_tasks;
    const chats = grantedChats;
    const hasAnything = docs.length > 0 || hasTasks || chats.length > 0;

    return (
      <div className="profile-company-users">
        <div className="profile-company-users-title manrope-bold">
          Доступ к разделам сервиса
        </div>
        {grantedChatsLoading && !hasAnything ? (
          <div className="profile-empty profile-empty--compact manrope-bold">
            Загрузка доступов…
          </div>
        ) : !hasAnything ? (
          <div className="profile-empty profile-empty--compact manrope-bold">
            Разделы не предоставлены
          </div>
        ) : (
          <>
            {docs.length > 0 ? (
              <div className="profile-access-block">
                <div className="profile-access-subtitle manrope-bold">Документы</div>
                {docs.map((section) => (
                  <div
                    key={section.id}
                    className="profile-access-row profile-access-row--readonly"
                  >
                    <span>{section.title}</span>
                  </div>
                ))}
              </div>
            ) : null}
            {hasTasks ? (
              <div className="profile-access-block">
                <div className="profile-access-subtitle manrope-bold">Задачи</div>
              </div>
            ) : null}
            {chats.length > 0 || grantedChatsLoading ? (
              <div className="profile-access-block">
                <div className="profile-access-subtitle manrope-bold">Чаты</div>
                {grantedChatsLoading ? (
                  <div className="profile-empty profile-empty--compact manrope-bold">
                    Загрузка…
                  </div>
                ) : (
                  chats.map((chat) => (
                    <div
                      key={chat.id}
                      className="profile-access-row profile-access-row--readonly"
                    >
                      <span>{chat.title}</span>
                    </div>
                  ))
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="profile-companies">
      <div className="section-page-heading manrope-bold color_white">Профиль</div>
      <div className="chat_page_header section-page_title_cntr">
        <button
          type="button"
          className="page-header-nav-back"
          onClick={handleListBack}
          aria-label="Назад"
        >
          {arrowLIcon}
        </button>
        <div className="manrope-bold color_white section-company-title">{headerTitle}</div>
        {showDetail ? (
          <button
            type="button"
            className="client_title-menu-btn"
            aria-label="Меню"
            onClick={() => setDetailMenuOpen(true)}
          >
            {dots3VerticalIcon}
          </button>
        ) : (
          <button
            type="button"
            className="client_title-menu-btn"
            aria-label="Меню"
            onClick={() => setMenuOpen(true)}
          >
            {dots3VerticalIcon}
          </button>
        )}
      </div>

      <div className="tasks_subcntr section-page-picker profile-body">
        {showUserAccess ? (
          <div className="profile-access-settings">
            {renderUserAccessNav()}
            {accessLoading || !accessDraft ? (
              <div className="profile-empty manrope-bold">Загрузка доступов…</div>
            ) : (
              <>
                <div className="profile-access-block">
                  <div className="profile-access-subtitle manrope-bold">Документы</div>
                  {DOC_ACCESS_SECTIONS.map((section) => (
                    <label key={section.id} className="profile-access-row">
                      <span>{section.title}</span>
                      <input
                        type="checkbox"
                        checked={!!accessDraft[section.id]}
                        onChange={(e) =>
                          setAccessDraft((prev) => ({
                            ...prev,
                            [section.id]: e.target.checked,
                          }))
                        }
                      />
                    </label>
                  ))}
                </div>

                <div className="profile-access-block">
                  <label className="profile-access-heading-row">
                    <span className="profile-access-subtitle manrope-bold">Задачи</span>
                    <input
                      type="checkbox"
                      checked={accessDraft.tasks}
                      onChange={(e) =>
                        setAccessDraft((prev) => ({ ...prev, tasks: e.target.checked }))
                      }
                    />
                  </label>
                </div>

                <div className="profile-access-block">
                  <div className="profile-access-subtitle manrope-bold">Чаты</div>
                  {accessDraft.chats.length === 0 ? (
                    <div className="profile-empty profile-empty--compact manrope-bold">
                      Чаты компании пока не созданы
                    </div>
                  ) : (
                    accessDraft.chats.map((chat) => (
                      <label key={chat.id} className="profile-access-row">
                        <span>{chat.title}</span>
                        <input
                          type="checkbox"
                          checked={chat.enabled}
                          onChange={(e) => {
                            const enabled = e.target.checked;
                            setAccessDraft((prev) => ({
                              ...prev,
                              chats: prev.chats.map((c) =>
                                c.id === chat.id ? { ...c, enabled } : c
                              ),
                            }));
                          }}
                        />
                      </label>
                    ))
                  )}
                </div>

                {accessDirty && (
                  <div className="profile-access-actions">
                    <button
                      type="button"
                      className="profile-add-company-btn manrope-bold"
                      style={{ background: '#94A3B8' }}
                      onClick={handleCancelAccess}
                      disabled={accessSaving}
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      className="profile-add-company-btn manrope-bold"
                      onClick={handleSaveAccess}
                      disabled={accessSaving}
                    >
                      {accessSaving ? 'Сохранение…' : 'Сохранить'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        ) : !showDetail ? (
          loading ? (
            <div className="profile-empty manrope-bold">Загрузка…</div>
          ) : companies.length === 0 ? (
            <div className="profile-empty manrope-bold">
              Компании пока не добавлены. Добавьте компанию по ИНН через меню.
            </div>
          ) : (
            <div className="company-picker-list company-picker-panel">
              {companies.map((user) => (
                <div
                  key={`${user.access_mode}-${user.company_id}`}
                  className="company-picker-item company-picker-item--profile-company"
                >
                  <button
                    type="button"
                    className="company-picker-main-btn company-picker-main-btn--profile-company manrope-bold"
                    onClick={() => handleSelect(user)}
                  >
                    <span className="company-picker-trailing">{arrowRIcon}</span>
                    <span className="company-picker-name">{user.company_name}</span>
                    {user.inn ? (
                      <div className="profile-employee-meta">ИНН: {user.inn}</div>
                    ) : null}
                    <div className="profile-company-card-meta">
                      <div className="profile-company-card-row">
                        <span className="profile-company-card-label">Статус</span>
                        <span className="profile-company-card-value profile-status-value">
                          <span>{getServiceStatusLabel(user.on_service)}</span>
                          {!asBoolFlag(user.on_service) ? renderServiceInfoHint() : null}
                        </span>
                      </div>
                      <div className="profile-company-card-row">
                        <span className="profile-company-card-label">Доступ</span>
                        <span className="profile-company-card-value">
                          {getAccessLabel(user)}
                        </span>
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )
        ) : selectedCompany ? (
          <>
            <div className="client_card_cntr manrope-bold">
              <div className="client_row">
                <div className="client_row-label color_grey">Название</div>
                <div className="client_row-value">
                  {selectedCompany.company_name || client?.name || '—'}
                </div>
              </div>
              <div className="client_row">
                <div className="client_row-label color_grey">ИНН</div>
                <div className="client_row-value">
                  {selectedCompany.inn || client?.inn || '—'}
                </div>
              </div>
              <div className="client_row">
                <div className="client_row-label color_grey">Адрес</div>
                <div className="client_row-value client_row-value--right">
                  {selectedCompany.address || client?.address || '—'}
                </div>
              </div>
              {renderCompanyInfoRows()}
              <div className="profile-company-extra-fields">
                <div className="client_row">
                  <div className="client_row-label color_grey">Email</div>
                  <div className="client_row-value">{client?.email}</div>
                </div>
                <div className="client_row">
                  <div className="client_row-label color_grey">Телефон</div>
                  <div className="client_row-value">{client?.phone}</div>
                </div>
                <div className="client_row">
                  <div className="client_row-label color_grey">Руководитель</div>
                  <div className="client_row-value client_row-value--right">{client?.ceo}</div>
                </div>
                <div className="client_row">
                  <div className="client_row-label color_grey">Налогооблажение</div>
                  <div className="client_row-value">{client?.tax}</div>
                </div>
              </div>
            </div>
            <div className="client_card2_cntr manrope-bold profile-company-extra-fields">
              <div className="client_row">
                <div style={{ fontSize: 14 }} className="color_white">
                  Бухгалтер
                </div>
                <div style={{ fontSize: 16 }} className="color_white">
                  {client?.acc_name}
                </div>
              </div>
              <div className="client_row">
                <div style={{ fontSize: 14 }} className="color_white">
                  Email
                </div>
                <div className="color_white client_label">{client?.acc_email}</div>
              </div>
              <div className="client_row">
                <div style={{ fontSize: 14 }} className="color_white">
                  Телефон
                </div>
                <div style={{ fontSize: 16 }} className="color_white">
                  {client?.acc_phone}
                </div>
              </div>
              {client?.acc_path ? (
                <div className="client_row">
                  <img className="client_acc_img" alt="" src={`${url}/${client.acc_path}`} />
                </div>
              ) : null}
            </div>
            {selectedCompany?.access_mode === 'delegated'
              ? renderGrantedAccessBlock()
              : renderCompanyUsersBlock()}
          </>
        ) : null}
      </div>

      {menuOpen && (
        <ClientMenuModal
          onClose={() => setMenuOpen(false)}
          items={[
            {
              label: 'Добавить компанию по ИНН',
              icon: iconWithPlus(addAccountIcon),
              onClick: () => setShowAddByInn(true),
            },
          ]}
        />
      )}

      {detailMenuOpen && selectedCompany && (
        <ClientMenuModal
          onClose={() => setDetailMenuOpen(false)}
          items={[
            {
              label: 'Удалить компанию из своего аккаунта',
              icon: <span className="profile-menu-delete-icon">{deleteIcon}</span>,
              danger: true,
              onClick: handleRemoveCompany,
            },
          ]}
        />
      )}

      {userAccessMenuOpen && accessEmployee && (
        <ClientMenuModal
          onClose={() => setUserAccessMenuOpen(false)}
          items={[
            {
              label: 'Удалить пользователя из этой компании',
              icon: <span className="profile-menu-delete-icon">{deleteIcon}</span>,
              danger: true,
              onClick: handleRemoveUserFromCompany,
            },
          ]}
        />
      )}

      {showAddByInn && businessAccountId && (
        <AddCompanyModal
          businessUserId={businessAccountId}
          onClose={() => setShowAddByInn(false)}
          onCreated={async () => {
            await loadCompanies();
            setShowDetail(false);
            setSelectedCompany(null);
          }}
        />
      )}

      {showAddUserModal && (
        <div className="modal-container profile-narrow-modal">
          <div className="modal-content">
            <div className="close-button" onClick={() => setShowAddUserModal(false)}>
              {closeIconTask}
            </div>
            <div style={{ textAlign: 'center' }} className="manrope-bold color_blue_1 fs-20">
              Выберите пользователя
            </div>
            <div style={{ width: '100%', marginTop: '20px' }}>
              {usersToAdd.length === 0 ? (
                <div className="profile-empty profile-empty--compact manrope-bold">
                  Нет доступных пользователей. Добавьте пользователя в разделе Пользователи.
                </div>
              ) : (
                <div className="company-picker-list company-picker-panel">
                  {usersToAdd.map((employee) => (
                    <div key={employee.id} className="company-picker-item">
                      <button
                        type="button"
                        className="company-picker-main-btn manrope-bold"
                        onClick={() => handleAddUserToCompany(employee)}
                        disabled={loading}
                      >
                        <div className="company-picker-main-top">
                          <span className="company-picker-name">{employee.displayName}</span>
                          <span className="company-picker-trailing profile-employee-trailing">
                            {employee.phone ? (
                              <span className="profile-employee-meta profile-employee-meta--inline">
                                Тел: {employee.phone}
                              </span>
                            ) : null}
                            {arrowRIcon}
                          </span>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProfileCompanies;
